import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "fireworks";

const DEFAULT_BG = "#070b1a";
const DEFAULT_BURST_A = "#fde68a";
const DEFAULT_BURST_B = "#f472b6";
const DEFAULT_BURST_C = "#22d3ee";

// Two-stage simulation:
//   1. Rockets launch from the bottom with an upward velocity, mild drag,
//      and a fuse timer. They draw a thin smoke trail.
//   2. When the fuse expires (or the rocket reaches its apex) the rocket
//      explodes into N sparks scattered radially with random speeds. Each
//      spark has gravity, drag, a randomized lifetime and a fading trail.
//
// We use a "trail fade" approach for the entire canvas — every frame we
// fill with a translucent background color, which automatically draws
// fading trails behind everything moving without per-particle bookkeeping.
const SPAWN_INTERVAL_MS = 850;
const SPAWN_JITTER_MS = 1100;
const TRAIL_FADE = 0.18;            // background fill alpha each frame
const ROCKET_FUSE_MIN = 800;
const ROCKET_FUSE_MAX = 1500;
const ROCKET_GRAVITY = 0.08;        // px/frame²
const ROCKET_DRAG = 0.992;
const SPARK_COUNT_MIN = 36;
const SPARK_COUNT_MAX = 80;
const SPARK_SPEED_MIN = 1.6;
const SPARK_SPEED_MAX = 4.2;
const SPARK_GRAVITY = 0.06;
const SPARK_DRAG = 0.978;
const SPARK_LIFE_MIN_MS = 700;
const SPARK_LIFE_MAX_MS = 1600;
const MAX_CONCURRENT_ROCKETS = 6;

function rand(min, max) { return min + Math.random() * (max - min); }
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

export default function Fireworks({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_BURST_A, DEFAULT_BURST_B, DEFAULT_BURST_C]);
  const rocketsRef = useRef([]);
  const sparksRef = useRef([]);
  const lastSpawnRef = useRef(0);
  const nextSpawnRef = useRef(SPAWN_INTERVAL_MS);
  const lastTsRef = useRef(0);
  const primedRef = useRef(false);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_BURST_A),
      pickColor(colors, 2, DEFAULT_BURST_B),
      pickColor(colors, 3, DEFAULT_BURST_C),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      primedRef.current = false;
    };

    const launchRocket = (ts) => {
      if (rocketsRef.current.length >= MAX_CONCURRENT_ROCKETS) return;
      const burstColors = colorsRef.current.slice(1);
      const targetY = rand(height * 0.12, height * 0.55);
      // Initial upward speed sized so the rocket comfortably reaches the
      // target altitude before its fuse expires.
      const flightTime = rand(ROCKET_FUSE_MIN, ROCKET_FUSE_MAX);
      // Solve for vy: y(t) = vy*t + 0.5*g*t² (in px/frame, with t in
      // frames). Rough estimate is fine — drag will smooth it.
      const frames = flightTime / 16.67;
      const dy = height - targetY;
      const vy = -(dy / frames + 0.5 * ROCKET_GRAVITY * frames);
      rocketsRef.current.push({
        x: rand(width * 0.10, width * 0.90),
        y: height + 4,
        vx: rand(-0.4, 0.4),
        vy,
        bornAt: ts,
        fuseMs: flightTime,
        color: pick(burstColors),
      });
    };

    const burst = (rocket, ts) => {
      const count = Math.floor(rand(SPARK_COUNT_MIN, SPARK_COUNT_MAX));
      for (let i = 0; i < count; i += 1) {
        const a = rand(0, Math.PI * 2);
        const sp = rand(SPARK_SPEED_MIN, SPARK_SPEED_MAX);
        sparksRef.current.push({
          x: rocket.x,
          y: rocket.y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          bornAt: ts,
          life: rand(SPARK_LIFE_MIN_MS, SPARK_LIFE_MAX_MS),
          color: rocket.color,
        });
      }
    };

    const tick = (ts) => {
      const [bg] = colorsRef.current;

      // Translucent fill = motion trails behind every rocket and spark.
      if (!primedRef.current) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);
        primedRef.current = true;
      } else {
        ctx.fillStyle = rgba(bg, TRAIL_FADE);
        ctx.fillRect(0, 0, width, height);
      }

      const dt = lastTsRef.current ? (ts - lastTsRef.current) : 16.67;
      lastTsRef.current = ts;
      // We tick physics at a target ~60fps step; if dt drifts (tab
      // switching) we cap to avoid huge integration jumps.
      const stepScale = Math.min(2, dt / 16.67);

      // Spawn cadence — pulled to top so even when off-screen rockets are
      // few, new ones consistently appear.
      if (!lastSpawnRef.current) lastSpawnRef.current = ts;
      if (ts - lastSpawnRef.current > nextSpawnRef.current) {
        launchRocket(ts);
        lastSpawnRef.current = ts;
        nextSpawnRef.current = SPAWN_INTERVAL_MS + rand(0, SPAWN_JITTER_MS);
      }

      // Update + render rockets.
      const rockets = rocketsRef.current;
      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i];
        r.vy += ROCKET_GRAVITY * stepScale;
        r.vx *= ROCKET_DRAG;
        r.vy *= ROCKET_DRAG;
        r.x += r.vx * stepScale;
        r.y += r.vy * stepScale;

        // Bright head — small core circle.
        ctx.fillStyle = rgba(r.color, 0.95);
        ctx.beginPath();
        ctx.arc(r.x, r.y, 1.8, 0, Math.PI * 2);
        ctx.fill();

        // Burst when fuse expires OR when the rocket starts falling
        // (apex), so we never explode below the visible peak.
        const fuseDone = ts - r.bornAt > r.fuseMs;
        const apexHit = r.vy >= 0;
        if (fuseDone || apexHit) {
          burst(r, ts);
          rockets.splice(i, 1);
        }
      }

      // Update + render sparks.
      const sparks = sparksRef.current;
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const s = sparks[i];
        const age = ts - s.bornAt;
        if (age > s.life) {
          sparks.splice(i, 1);
          continue;
        }
        s.vy += SPARK_GRAVITY * stepScale;
        s.vx *= SPARK_DRAG;
        s.vy *= SPARK_DRAG;
        s.x += s.vx * stepScale;
        s.y += s.vy * stepScale;

        const ageT = age / s.life;            // 0 → 1 over lifetime
        const alpha = (1 - ageT) ** 1.4;      // bias to fade slowly then drop fast
        const r = 1.6 * (1 - ageT * 0.6);

        // Halo + core for the glowing ember look.
        const haloR = r * 5;
        const halo = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, haloR);
        halo.addColorStop(0, rgba(s.color, alpha * 0.55));
        halo.addColorStop(1, rgba(s.color, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(s.x, s.y, haloR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = rgba(s.color, alpha);
        ctx.beginPath();
        ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    // Pre-launch a couple of rockets so the canvas isn't empty when
    // first mounted.
    launchRocket(performance.now());
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
